import '../PasswordInput.scss';

import { mdiCheck, mdiClose } from '@mdi/js';
import IconText from 'components/IconText/IconText';
import { useCustomTranslation } from 'helpers/translationHelper';
import { COMPLEX_PASSWORD_LENGTH, MIN_PASSWORD_LENGTH, useValidators } from 'helpers/validation';
import { type RegisterOptions, useFormContext } from 'react-hook-form';

import TextInput from './TextInput';

interface PasswordInputProps {
  name: string;
  label?: string;
  required?: boolean;
  options?: RegisterOptions;
  requireComplexPasswords?: boolean;
}

const PasswordInput = ({
  name,
  label,
  options,
  required,
  requireComplexPasswords
}: PasswordInputProps) => {
  const { tSetting } = useCustomTranslation();
  const { watch } = useFormContext<{ [name]: string }>();

  const password = watch(name);

  const validators = useValidators();

  return (
    <div>
      <TextInput
        options={{
          ...options,
          validate: requireComplexPasswords
            ? validators.passwordComplexityValidation
            : options?.validate,
          minLength: validators.minLength(
            requireComplexPasswords ? COMPLEX_PASSWORD_LENGTH : MIN_PASSWORD_LENGTH
          )
        }}
        name={name}
        type="password"
        label={label}
        required={required}
      />
      {requireComplexPasswords && password && (
        <div>
          {/* Length */}
          <IconText
            iconPath={
              validators.passwordMinLength(
                password,
                requireComplexPasswords ? COMPLEX_PASSWORD_LENGTH : MIN_PASSWORD_LENGTH
              )
                ? mdiClose
                : mdiCheck
            }
            iconType={
              validators.passwordMinLength(
                password,
                requireComplexPasswords ? COMPLEX_PASSWORD_LENGTH : MIN_PASSWORD_LENGTH
              )
                ? 'danger'
                : 'success'
            }
            text={tSetting('password_min_length', { length: COMPLEX_PASSWORD_LENGTH })}
          />
          {/* Lowercase */}
          <IconText
            iconPath={validators.passwordLowercaseChar(password) ? mdiClose : mdiCheck}
            iconType={validators.passwordLowercaseChar(password) ? 'danger' : 'success'}
            text={tSetting('password_lower_case_char')}
          />
          {/* Uppercase */}
          <IconText
            iconPath={validators.passwordUppercaseChar(password) ? mdiClose : mdiCheck}
            iconType={validators.passwordUppercaseChar(password) ? 'danger' : 'success'}
            text={tSetting('password_upper_case_char')}
          />
          {/* Number */}
          <IconText
            iconPath={validators.passwordNumberChar(password) ? mdiClose : mdiCheck}
            iconType={validators.passwordNumberChar(password) ? 'danger' : 'success'}
            text={tSetting('password_number_char')}
          />
          {/* Special char */}
          <IconText
            iconPath={validators.passwordSpecialChar(password) ? mdiClose : mdiCheck}
            iconType={validators.passwordSpecialChar(password) ? 'danger' : 'success'}
            text={tSetting('password_special_char')}
          />
        </div>
      )}
    </div>
  );
};

export default PasswordInput;
